import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { providerSettlementsApi, actionMessage } from '../../services/providerSettlementsApi';
import { RecoveryProvider } from '../../types/delivery';
import {
  ProviderSettlement,
  TripPeriod,
  UnsettledProviderTrips,
  dubaiToday,
  formatTripDate,
  legLabel,
  monthLabel,
  vatIncluded,
} from '../../types/providerSettlements';
import Money, { formatAed } from '../finance/Money';
import InfoTip from '../finance/InfoTip';

interface UnsettledTripsPanelProps {
  providerId: string;
  /** The registry entry, when the registry could be read — its VAT status pre-fills the pay form. */
  provider?: RecoveryProvider | null;
  /** Dubai dates, inclusive; `from` omitted = every unsettled trip up to `to`. */
  period: TripPeriod;
  /**
   * How the period reads to the admin: a month ("September 2026") when `period.from` is set, else
   * the open-ended form ("up to 15 Sept 2026"), which already carries its own preposition.
   */
  periodLabel: string;
  /** Bumped by the parent after anything that moves trips in or out of the pool (a void, a payment elsewhere). */
  refreshKey: number;
  /** Called with the new PAID settlement after "Mark as paid" succeeds. */
  onPaid: (settlement: ProviderSettlement) => void;
}

/**
 * Paying a recovery provider for a period (Maher, 2026-09-27).
 *
 * The admin picks a provider and a period (usually a month) and sees what is owed: how many orders,
 * how many trips, and every trip linked to its order so the pickup and drop-off can be checked. Then
 * **Mark as paid**: the amount is mandatory; the invoice is optional and can be attached later from
 * the settlement. The trips are settled in the same step — no draft is left behind.
 *
 * The VAT is decided here, not from the trips' frozen snapshots: the pay form pre-ticks "VAT
 * registered" from the provider registry (or, when the registry is out of reach, from the newest
 * trip), the admin can change it, and the server claims amount × 5/105 in the payment month's VAT
 * return when it is ticked. A provider that registered mid-month therefore no longer loses the
 * whole month's VAT to the first trip's snapshot.
 */
export default function UnsettledTripsPanel({ providerId, provider, period, periodLabel, refreshKey, onPaid }: UnsettledTripsPanelProps) {
  const auth = useAuth();
  const [pool, setPool] = useState<UnsettledProviderTrips | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  // Survives the reload a 409 "trips changed" triggers, so the admin sees why the list moved.
  const [notice, setNotice] = useState<string | null>(null);

  const [paying, setPaying] = useState(false);
  const [busy, setBusy] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const [amount, setAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(dubaiToday());
  const [reference, setReference] = useState('');
  const [vatRegistered, setVatRegistered] = useState(false);
  const [invoice, setInvoice] = useState<File | null>(null);

  const { from, to } = period;
  // "in September 2026" / "up to 15 Sept 2026" — never "in up to …".
  const periodPhrase = from ? `in ${periodLabel}` : periodLabel;
  const periodTitle = periodLabel.charAt(0).toUpperCase() + periodLabel.slice(1);

  useEffect(() => { setNotice(null); }, [providerId, from, to]);

  useEffect(() => {
    if (!auth.token) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPool(null);
    setPaying(false);
    setPayError(null);
    providerSettlementsApi.unsettledTrips(providerId, auth.token, { from, to })
      .then((p) => { if (!cancelled) setPool(p); })
      .catch((err) => { if (!cancelled) setError(actionMessage(err, 'Failed to load the unsettled trips')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [auth.token, providerId, from, to, refreshKey, reload]);

  // The registry is the provider's CURRENT status; without it, the newest trip's snapshot is the
  // best available guess. Either way it is only a pre-fill — the admin confirms it.
  const newestTrip = pool && pool.trips.length > 0 ? pool.trips[pool.trips.length - 1] : null;
  const defaultVat = provider ? provider.vatRegistered : newestTrip?.providerVatRegistered === true;
  const trn = provider ? provider.trn : newestTrip?.providerTrn ?? null;

  const openPayForm = () => {
    setPaying(true);
    setPayError(null);
    setAmount('');
    setReference('');
    setInvoice(null);
    setPaymentDate(dubaiToday());
    setVatRegistered(defaultVat);
  };

  const parsedAmount = Number(amount);
  const amountValid = amount.trim() !== '' && Number.isFinite(parsedAmount) && parsedAmount > 0;
  const vat = amountValid && vatRegistered ? vatIncluded(parsedAmount) : 0;

  const markPaid = async (e: FormEvent) => {
    e.preventDefault();
    if (!auth.token || !pool || busy || !to) return;
    if (!amountValid) { setPayError('Enter the amount paid'); return; }
    setBusy(true);
    setPayError(null);
    setNotice(null);
    try {
      const paid = await providerSettlementsApi.payPeriod({
        providerId,
        from,
        to,
        tripCount: pool.tripCount,
        amount: parsedAmount,
        providerVatRegistered: vatRegistered,
        providerTrn: vatRegistered ? trn : null,
        paymentDate,
        reference: reference.trim() || undefined,
        invoice,
      }, auth.token);
      setPaying(false);
      onPaid(paid.settlement);
    } catch (err) {
      const message = actionMessage(err, 'Failed to record the payment');
      if (/changed since you loaded/.test(message)) {
        // 409 — reload the list so the admin checks the trips actually being paid before retrying.
        setNotice(message);
        setReload((n) => n + 1);
      } else {
        setPayError(message);
      }
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-loading" data-testid="unsettled-loading" style={{ marginTop: '0.75rem' }}>
        <div className="admin-loading-spinner"></div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="admin-info-banner danger" role="alert" data-testid="unsettled-error" style={{ marginTop: '0.75rem' }}>
        {error}
      </div>
    );
  }
  if (!pool) return null;

  const olderNote = pool.olderTripCount > 0 && (
    <div className="admin-info-banner info" role="status" data-testid="unsettled-older" style={{ marginTop: '0.75rem' }}>
      {pool.olderTripCount} older unpaid trip{pool.olderTripCount === 1 ? '' : 's'} completed before this period.
      Choose “Up to a date” to include {pool.olderTripCount === 1 ? 'it' : 'them'}.
    </div>
  );

  if (pool.tripCount === 0 || pool.trips.length === 0) {
    return (
      <>
        {olderNote}
        <div className="admin-empty-state" data-testid="unsettled-empty" style={{ marginTop: '0.75rem' }}>
          <p>No unpaid trips for this provider {periodPhrase}.</p>
        </div>
      </>
    );
  }

  return (
    <div data-testid="unsettled-trips" style={{ marginTop: '0.75rem' }}>
      {notice && (
        <div className="admin-info-banner danger" role="alert" data-testid="unsettled-changed" style={{ marginBottom: '0.75rem' }}>
          {notice}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem' }} data-testid="unsettled-summary">
        <div style={{ padding: '0.75rem', background: 'var(--admin-bg)', borderRadius: '6px' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>Period</div>
          <div style={{ fontSize: '1rem', fontWeight: 700, marginTop: '0.25rem' }} data-testid="unsettled-period">{periodTitle}</div>
        </div>
        <div style={{ padding: '0.75rem', background: 'var(--admin-bg)', borderRadius: '6px' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>Orders</div>
          <div style={{ fontSize: '1rem', fontWeight: 700, marginTop: '0.25rem' }} data-testid="unsettled-order-count">{pool.orderCount}</div>
        </div>
        <div style={{ padding: '0.75rem', background: 'var(--admin-bg)', borderRadius: '6px' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', display: 'flex', alignItems: 'center' }}>
            Trips<InfoTip glossaryKey="providerSettlementTripCount" />
          </div>
          <div style={{ fontSize: '1rem', fontWeight: 700, marginTop: '0.25rem' }} data-testid="unsettled-trip-count">{pool.tripCount}</div>
        </div>
        {pool.total != null && pool.total > 0 && (
          <div style={{ padding: '0.75rem', background: 'var(--admin-bg)', borderRadius: '6px' }} data-testid="unsettled-total">
            <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', display: 'flex', alignItems: 'center' }}>
              Agreed trip costs<InfoTip glossaryKey="providerSettlementAmount" />
            </div>
            <div style={{ fontSize: '1rem', fontWeight: 700, marginTop: '0.25rem' }}>
              <Money amount={pool.total} glossaryKey="providerSettlementAmount" hideTip />
            </div>
          </div>
        )}
      </div>

      {olderNote}

      <div className="admin-table-wrapper" style={{ marginTop: '0.75rem' }}>
        <table className="admin-table" data-testid="unsettled-trips-table">
          <thead>
            <tr>
              <th>Order</th>
              <th>Leg</th>
              <th>Completed</th>
              <th>Driver</th>
            </tr>
          </thead>
          <tbody>
            {pool.trips.map((trip) => (
              <tr key={trip.tripId} data-testid="unsettled-trip-row">
                <td style={{ fontWeight: 500 }}>
                  {/* The order page has the pickup and drop-off details; a new tab keeps this list. */}
                  <Link className="admin-link" to={`/orders/${trip.orderId}`} target="_blank" rel="noreferrer"
                    data-testid="unsettled-order-link">
                    {trip.orderNumber ?? `${trip.orderId.slice(0, 8)}…`}
                  </Link>
                </td>
                <td>{legLabel(trip.leg)}</td>
                <td>{formatTripDate(trip.completedAt)}</td>
                <td>{trip.driverName ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!paying && (
        <div style={{ marginTop: '0.75rem' }}>
          <button type="button" className="admin-btn admin-btn-sm admin-btn-primary" onClick={openPayForm}>
            Mark as paid
          </button>
        </div>
      )}

      {paying && (
        <form className="admin-card" style={{ marginTop: '0.75rem' }} onSubmit={markPaid} data-testid="pay-period-form">
          <p style={{ fontSize: '0.8125rem', color: 'var(--admin-text-muted)', marginTop: 0 }}>
            Pays all {pool.tripCount} trip{pool.tripCount === 1 ? '' : 's'} ({pool.orderCount} order{pool.orderCount === 1 ? '' : 's'}) {periodPhrase}.
          </p>
          <div className="form-group">
            <label htmlFor="pay-period-amount">Amount paid (AED, VAT included)</label>
            <input id="pay-period-amount" type="number" step="0.01" min="0" className="form-input" value={amount}
              aria-required="true" onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 400 }}>
              <input type="checkbox" checked={vatRegistered} data-testid="pay-period-vat-registered"
                onChange={(e) => setVatRegistered(e.target.checked)} />
              Provider is VAT-registered{trn ? ` (TRN ${trn})` : ''}
            </label>
            <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
              data-testid="pay-period-vat">
              {vatRegistered
                ? <>VAT claimed: {amountValid ? formatAed(vat) : '—'} (5/105 of the amount), in the {monthLabel(paymentDate)} VAT return</>
                : 'No VAT is claimed on this payment.'}
              <InfoTip glossaryKey="providerSettlementVatClaimed" />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="pay-period-date">Payment date</label>
            <input id="pay-period-date" type="date" className="form-input" value={paymentDate} max={dubaiToday()}
              onChange={(e) => setPaymentDate(e.target.value)} />
          </div>
          <div className="form-group">
            <label htmlFor="pay-period-reference">Payment reference (optional)</label>
            <input id="pay-period-reference" type="text" className="form-input" value={reference} maxLength={100}
              onChange={(e) => setReference(e.target.value)} />
          </div>
          <div className="form-group">
            <label htmlFor="pay-period-invoice">Provider invoice (optional — PDF, PNG, JPG)</label>
            <input id="pay-period-invoice" type="file" accept=".pdf,.png,.jpg,.jpeg"
              onChange={(e) => setInvoice(e.target.files?.[0] ?? null)} />
            <div style={{ fontSize: '0.6875rem', color: 'var(--admin-text-muted)' }}>
              You can attach it later from the settlement.
            </div>
          </div>
          {payError && <div className="admin-info-banner danger" role="alert" data-testid="pay-period-error">{payError}</div>}
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="submit" className="admin-btn admin-btn-sm admin-btn-primary" disabled={busy}>
              {busy ? 'Recording…' : 'Confirm payment'}
            </button>
            <button type="button" className="admin-btn admin-btn-sm admin-btn-outline" disabled={busy}
              onClick={() => setPaying(false)}>Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
}
